import { useState, useEffect, useRef, useCallback } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import {
  X,
  AlertCircle,
  MapPin,
  Gauge,
  RefreshCw,
} from "lucide-react";

import {
  fetchLiveVehicleByNumber,
  fmtTimestamp,
  statusLabel,
  statusTone,
} from "../../services/fleetx_service";

import "leaflet/dist/leaflet.css";
import "./VehicleLiveMap.css";

const POLL_MS = 5000;

const TONE_COLOR = {
  positive: "#178A4C",
  neutral: "#B4780F",
  negative: "#C6303E",
  idle: "#64748B",
  default: "#64748B",
};

function markerIcon(tone, heading) {
  const color =
    TONE_COLOR[tone] || TONE_COLOR.default;

  const rawHeading =
    typeof heading === "number" &&
    Number.isFinite(heading)
      ? heading
      : 0;

  const rotation =
    (rawHeading + 180) % 360;

  const html = `
    <div
      class="vlm-car-marker"
      style="--vlm-marker-rotation: ${rotation}deg;"
    >
      <img
        src="/assets/carmap.png"
        alt="Vehicle"
        class="vlm-car-image"
      />
    </div>
  `;

  return L.divIcon({
    html,
    className: "vlm-marker-icon",
    iconSize: [60, 60],
    iconAnchor: [30, 30],
    popupAnchor: [0, -30],
  });
}

function MapController({ onReady }) {
  const map = useMap();

  useEffect(() => {
    onReady(map);
  }, [map, onReady]);

  return null;
}

export default function VehicleLiveMap({
  vehicleNumber,
  apiBaseUrl,
  getToken,
  onClose,
}) {
  const mapRef = useRef(null);
  const pollRef = useRef(null);
  const hasCenteredRef = useRef(false);

  const [vehicle, setVehicle] = useState(null);
  const [error, setError] = useState(null);
  const [initialLoading, setInitialLoading] =
    useState(true);

  const handleMapReady = useCallback((map) => {
    mapRef.current = map;

    setTimeout(() => {
      map.invalidateSize();
    }, 100);
  }, []);

  const poll = useCallback(async () => {
    try {
      const data =
        await fetchLiveVehicleByNumber(
          apiBaseUrl,
          getToken,
          vehicleNumber
        );

      setVehicle(data);
      setError(null);

      if (
        mapRef.current &&
        typeof data.latitude === "number" &&
        typeof data.longitude === "number"
      ) {
        const position = [
          data.latitude,
          data.longitude,
        ];

        if (!hasCenteredRef.current) {
          mapRef.current.setView(position, 17, {
            animate: true,
          });

          hasCenteredRef.current = true;
        } else {
          mapRef.current.panTo(position, {
            animate: true,
            duration: 0.5,
          });
        }
      }
    } catch (e) {
      console.error(
        "Failed to fetch live vehicle:",
        e
      );

      setError(
        e?.message ||
          "Unable to update vehicle location"
      );
    } finally {
      setInitialLoading(false);
    }
  }, [
    apiBaseUrl,
    getToken,
    vehicleNumber,
  ]);

  useEffect(() => {
    hasCenteredRef.current = false;

    poll();

    pollRef.current = setInterval(
      poll,
      POLL_MS
    );

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
      }
    };
  }, [poll]);

  const hasPosition =
    vehicle &&
    typeof vehicle.latitude === "number" &&
    typeof vehicle.longitude === "number";

  return (
    <div
      className="vlm-overlay"
      onClick={onClose}
    >
      <div
        className="vlm-card"
        onClick={(e) =>
          e.stopPropagation()
        }
      >
        <div className="vlm-header">
          <div className="vlm-header-info">
            <h3 className="vlm-title">
              {vehicleNumber}
            </h3>

            {vehicle?.vehicleName && (
              <p className="vlm-subtitle">
                {vehicle.vehicleName}
              </p>
            )}
          </div>

          <div className="vlm-header-right">
            <span className="vlm-live-badge">
              <span className="vlm-live-dot" />
              Live
            </span>

            <button
              type="button"
              className="vlm-close-btn"
              onClick={onClose}
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {vehicle && (
          <div className="vlm-status-row">
            <span
              className={`vlm-pill vlm-pill--${statusTone(
                vehicle.currentStatus
              )}`}
            >
              <span className="vlm-pill-dot" />

              {statusLabel(
                vehicle.currentStatus
              )}
            </span>

            <span className="vlm-meta">
              <Gauge size={13} />

              {typeof vehicle.speed ===
              "number"
                ? `${Math.round(
                    vehicle.speed
                  )} km/h`
                : "—"}
            </span>

            <span className="vlm-meta">
              Updated{" "}
              {fmtTimestamp(
                vehicle.lastUpdatedAt
              )}
            </span>
          </div>
        )}

        {error && (
          <div className="vlm-error-banner">
            <AlertCircle size={14} />

            <span>
              {error} — showing last known
              position, retrying…
            </span>
          </div>
        )}

        {initialLoading ? (
          <div className="vlm-map-fallback">
            <RefreshCw
              size={16}
              className="vlm-spin"
            />

            <span>
              Locating vehicle…
            </span>
          </div>
        ) : !hasPosition ? (
          <div className="vlm-map-fallback">
            <MapPin size={16} />

            <span>
              No location data for this
              vehicle yet.
            </span>
          </div>
        ) : (
          <div className="vlm-map-canvas">
            <MapContainer
              className="vlm-gmap"
              center={[
                vehicle.latitude,
                vehicle.longitude,
              ]}
              zoom={17}
              minZoom={5}
              maxZoom={19}
              scrollWheelZoom={true}
              doubleClickZoom={true}
              dragging={true}
              touchZoom={true}
              zoomControl={true}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                maxZoom={19}
                maxNativeZoom={19}
                updateWhenIdle={true}
                updateWhenZooming={false}
                keepBuffer={3}
              />

              <MapController
                onReady={handleMapReady}
              />

              <Marker
                position={[
                  vehicle.latitude,
                  vehicle.longitude,
                ]}
                icon={markerIcon(
                  statusTone(
                    vehicle.currentStatus
                  ),
                  vehicle.heading
                )}
              />
            </MapContainer>
          </div>
        )}

        {vehicle?.address && (
          <div className="vlm-address">
            <MapPin size={13} />

            <span>
              {vehicle.address}
            </span>
          </div>
        )}

        <div className="vlm-poll-note">
          Refreshes every{" "}
          {POLL_MS / 1000}s automatically
        </div>
      </div>
    </div>
  );
}
